import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import SignaturePad from "@/components/SignaturePad";
import { HelpVideo } from "@/components/HelpVideo";
import { Loader2, FileSignature, CheckCircle2, Download, ShieldCheck, Mail, ChevronDown } from "lucide-react";
import { toast } from "sonner";

/** Tela cheia: o aluno lê o contrato, confirma os dados, assina e só então é liberado para a plataforma. */
export default function SignContract({ preview = false }) {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [lido, setLido] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [aceitoC, setAceitoC] = useState(false);
  const [aceitoP, setAceitoP] = useState(false);
  const [nome, setNome] = useState("");           // nome digitado na etapa 3 (confirmação)
  const [nomeCad, setNomeCad] = useState("");       // nome completo da etapa 2 (a aluna pode corrigir)
  const [cpf, setCpf] = useState("");
  const [fone, setFone] = useState("");             // telefone / WhatsApp (a aluna completa se faltar)
  const [assin, setAssin] = useState(null);
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState("");
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState("");
  const [feito, setFeito] = useState(null);
  const [aut, setAut] = useState(null);   // Autentique: { link } enquanto aguarda a assinatura
  const box = useRef(null);
  const fim = useRef(null);

  const carregar = useCallback(() => api.get("/me/contract").then((r) => { setC(r.data); setNomeCad(r.data.signer?.name || ""); setFone(r.data.signer?.phone || ""); setLido(false); setProgresso(0); }).catch((e) => setErro(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { carregar(); }, [carregar]);

  const conferir = useCallback(async (agora, silencioso) => {
    try {
      const r = await api.get("/me/contract/status", { params: agora ? { check: 1 } : {} });
      if (r.data.state === "assinado" && r.data.user) { setFeito({ code: "", user: r.data.user, autentique: true }); return true; }
      if (agora && !silencioso) toast.info("Ainda não recebemos a sua assinatura. Conclua na página da Autentique e tente de novo em alguns segundos.");
    } catch (e) { /* tenta de novo no próximo ciclo */ }
    return false;
  }, []);
  useEffect(() => {
    if (!aut) return undefined;
    // a cada 4 s olha o nosso banco; a cada ~16 s e ao voltar para esta aba, confere direto na Autentique (não depende do aviso automático)
    let n = 0;
    const t = setInterval(() => { n += 1; conferir(n % 4 === 0, true); }, 4000);
    const v = () => { if (document.visibilityState === "visible") conferir(true, true); };
    const f = () => conferir(true, true);
    document.addEventListener("visibilitychange", v); window.addEventListener("focus", f);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", v); window.removeEventListener("focus", f); };
  }, [aut, conferir]);

  // O contrato agora aparece inteiro na tela (sem caixa com rolagem própria): rolar a tela já basta.
  // "Leu até o final" = o fim do texto apareceu na tela; a barra acompanha a posição do fim do texto.
  const checarRolagem = useCallback(() => {
    const el = box.current, f = fim.current; if (!el || !f) return;
    const r = el.getBoundingClientRect(), fr = f.getBoundingClientRect(), vh = window.innerHeight;
    const total = fr.top - r.top;
    const p = total <= 0 ? 100 : Math.max(0, Math.min(100, Math.round(((vh - r.top) / total) * 100)));
    setProgresso(p); if (fr.top <= vh - 4) setLido(true);
  }, []);
  useEffect(() => {
    if (!c) return undefined;
    const t = setTimeout(checarRolagem, 100);
    // a rolagem acontece dentro da tela fixa da assinatura, não na janela: ouvir em qualquer elemento (captura)
    document.addEventListener("scroll", checarRolagem, true);
    window.addEventListener("resize", checarRolagem);
    let io;
    if (fim.current && "IntersectionObserver" in window) { io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setLido(true); setProgresso(100); } }); io.observe(fim.current); }
    return () => { clearTimeout(t); document.removeEventListener("scroll", checarRolagem, true); window.removeEventListener("resize", checarRolagem); io && io.disconnect(); };
  }, [c, checarRolagem]);

  if (feito) {
    return (
      <Shell onLogout={logout}>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center max-w-xl mx-auto" data-testid="contract-done">
          <CheckCircle2 className="h-14 w-14 text-[#1E9E2A] mx-auto" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Contrato assinado!</h1>
          <p className="text-slate-500 mt-2">Sua assinatura foi registrada e uma cópia foi enviada para o seu e-mail.</p>
          {feito.code && <><p className="text-xs text-slate-400 mt-4">Código de verificação</p>
          <p className="font-mono text-lg font-semibold text-slate-800">{feito.code}</p></>}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
            <Button variant="outline" onClick={baixar} data-testid="contract-download"><Download className="h-4 w-4 mr-1.5" /> Baixar cópia (PDF)</Button>
            <Button className="text-white" style={{ background: "#1E9E2A" }} onClick={() => { setUser(feito.user); navigate("/", { replace: true }); }} data-testid="contract-continue">Entrar na plataforma</Button>
          </div>
        </div>
      </Shell>
    );
  }

  async function baixar() {
    try {
      const r = await api.get("/me/contract/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `contrato-assinado${feito?.code ? "-" + feito.code : ""}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Não foi possível baixar agora."); }
  }

  if (!c) return <Shell onLogout={preview ? () => navigate("/app") : logout} preview={preview}><div className="py-24 grid place-items-center">{erro ? <p className="text-red-600">{erro}</p> : <Loader2 className="h-7 w-7 animate-spin text-slate-400" />}</div></Shell>;
  if (!c.pending && !preview) { setTimeout(() => navigate("/", { replace: true }), 0); return null; }
  const sair = preview ? () => navigate("/app") : logout;

  const precisaDoc = c.signer.needs_document;
  const norm = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
  const cadOk = nomeCad.trim().split(/\s+/).filter(Boolean).length >= 2;
  const foneOk = fone.replace(/\D/g, "").length >= 10;
  const nomeOk = cadOk && norm(nome) === norm(nomeCad);
  const docOk = !precisaDoc || cpf.replace(/\D/g, "").length >= (c.signer.foreign ? 5 : 11);
  const codigoOk = !c.require_code || code.trim().length === 6;
  const externo = c.provider === "autentique";
  const pronto = externo ? (lido && aceitoC && aceitoP && docOk && cadOk && foneOk) : (lido && aceitoC && aceitoP && nomeOk && foneOk && docOk && !!assin && codigoOk);
  // o que ainda falta para o botão liberar (a aluna não precisa adivinhar)
  const faltas = [];
  if (!lido) faltas.push("Rolar a tela para baixo até o final do contrato (aparece \"Leitura concluída\" quando chegar ao fim)");
  else if (!aceitoC || !aceitoP) faltas.push("Marcar as duas confirmações (contrato e política de privacidade)");
  if (!externo && !nomeOk) faltas.push(cadOk ? `Digitar seu nome completo igual ao do cadastro: ${nomeCad}` : "Conferir seu nome completo (nome e sobrenome)");
  if (externo && !cadOk) faltas.push("Conferir seu nome completo (nome e sobrenome)");
  if (!foneOk) faltas.push("Informar seu celular com DDD");
  if (!docOk) faltas.push(c.signer.foreign ? "Informar seu documento" : "Informar seu CPF (11 números)");
  if (!externo && !assin) faltas.push("Desenhar sua assinatura no quadro tracejado, com o dedo");
  if (!externo && !codigoOk) faltas.push("Digitar o código de 6 números enviado para o seu e-mail");

  const iniciarExterno = async () => {
    setBusy(true); setErro("");
    try {
      const r = await api.post("/me/contract/start", { accept_contract: aceitoC, accept_privacy: aceitoP, read_to_end: lido, document: cpf, name: nomeCad, phone: fone, text_hash: c.text_hash });
      setAut({ link: r.data.link });
      if (r.data.link) window.open(r.data.link, "_blank", "noopener");
    } catch (e) { const d = formatApiError(e.response?.data?.detail); setErro(d); if (e.response?.status === 409) carregar(); } finally { setBusy(false); }
  };

  const enviarCodigo = async () => {
    setBusy(true); setErro("");
    try { const r = await api.post("/me/contract/code"); setCodeSent(r.data.email); toast.success("Código enviado para " + r.data.email); }
    catch (e) { setErro(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const assinar = async () => {
    setBusy(true); setErro("");
    try {
      const r = await api.post("/me/contract/sign", { signature: assin, typed_name: nome, name: nomeCad, phone: fone, accept_contract: aceitoC, accept_privacy: aceitoP, read_to_end: lido, document: cpf, code, text_hash: c.text_hash });
      setFeito({ code: r.data.code, user: r.data.user });
    } catch (e) {
      const d = formatApiError(e.response?.data?.detail); setErro(d);
      if (e.response?.status === 409) carregar();
    } finally { setBusy(false); }
  };

  return (
    <Shell onLogout={sair} preview={preview}>
      <div className="max-w-3xl mx-auto space-y-5">
        {preview && <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="contract-preview-banner">
          👁️ <strong>Pré-visualização do administrador:</strong> é assim que {c.signer?.name || "a aluna"} vê a tela de assinatura. A assinatura só pode ser feita por ela, na conta dela.</div>}
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2"><FileSignature className="h-7 w-7 text-[#1E9E2A]" /> Assinatura do contrato</h1>
          <p className="text-slate-500 mt-1.5 text-sm sm:text-base">Para acessar seus cursos, leia o contrato, confirme seus dados e assine. Leva poucos minutos e você recebe uma cópia por e-mail.</p>
          <HelpVideo key={c?.provider || "x"} moment={c?.provider === "autentique" ? "contrato_biometria" : "contrato"} label="Ver vídeo de como assinar" title="Veja como assinar (1 minuto)" preview={preview} className="mt-2" />
        </div>

        <Passo n={1} titulo="Leia o contrato" ok={lido}>
          <div ref={box} className="lesson-html rounded-xl border border-slate-200 bg-white p-4 sm:p-5 text-[14px] text-slate-700 leading-relaxed" data-testid="contract-text"
            dangerouslySetInnerHTML={{ __html: c.html }} />
          <div ref={fim} className="h-1" aria-hidden="true" />
          <div className="sticky bottom-3 z-20 mt-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-3 py-2 shadow-sm">
            <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#1E9E2A] transition-all" style={{ width: `${progresso}%` }} /></div>
            <span className={`text-xs whitespace-nowrap ${lido ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>{lido ? "Leitura concluída ✓" : "Role a tela até o final do contrato"}</span>
          </div>
          {c.privacy_html && (
            <details className="mt-3 rounded-xl border border-slate-200 bg-white">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-slate-700 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#1E9E2A]" /> Política de Privacidade <ChevronDown className="h-4 w-4 ml-auto text-slate-400" /></summary>
              <div className="lesson-html max-h-72 overflow-y-auto px-4 pb-4 text-[14px] text-slate-600" dangerouslySetInnerHTML={{ __html: c.privacy_html }} />
            </details>
          )}
        </Passo>

        <Passo n={2} titulo="Confirme seus dados" ok={cadOk && foneOk && docOk}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Campo label="Nome completo"><Input value={nomeCad} onChange={(e) => setNomeCad(e.target.value)} autoComplete="name" placeholder="Digite seu nome completo" data-testid="contract-name" /></Campo>
            <Campo label={c.signer.foreign ? "Documento" : "CPF"}>
              {precisaDoc ? <Input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder={c.signer.foreign ? "Documento de identificação" : "000.000.000-00"} inputMode="numeric" data-testid="contract-cpf" />
                : <Input value={c.signer.document} readOnly className="bg-slate-50" />}
            </Campo>
            <Campo label="E-mail"><Input value={c.signer.email} readOnly className="bg-slate-50" /></Campo>
            <Campo label="Telefone / WhatsApp"><Input value={fone} onChange={(e) => setFone(e.target.value)} placeholder="(85) 99999-9999" inputMode="tel" autoComplete="tel" data-testid="contract-phone" /></Campo>
          </div>
          <p className="text-xs text-slate-400 mt-2">Escreva seu nome completo, como nos seus documentos (pode corrigir se estiver incompleto). Confira também o telefone / WhatsApp (digite se estiver em branco). Se o e-mail estiver errado, fale com a equipe antes de assinar.</p>
        </Passo>

        {externo ? (
        <Passo n={3} titulo="Assine pela Autentique" ok={false}>
          {!aut ? (
            <>
              <p className="text-sm text-slate-600">Você vai assinar em uma página segura da <strong>Autentique</strong>, plataforma de assinatura eletrônica. A cópia do contrato assinado chega ao seu e-mail ({c.signer.email}) e fica disponível em Minha conta.</p>
              {(c.verification === "selfie_doc" || c.verification === "selfie_doc_vivo") && (
                <p className="mt-2 text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="contract-biometric-note">
                  <strong>Verificação de identidade:</strong> para proteger você e a escola contra fraudes, a Autentique vai pedir a <strong>foto de um documento com foto</strong> (RG ou CNH) e uma <strong>selfie</strong>, conferidas por reconhecimento facial{c.verification === "selfie_doc_vivo" ? ", com prova de vida" : ""}. Esses dados são usados só para confirmar a sua identidade na assinatura (Cláusula 12 do contrato e Política de Privacidade). Tenha o documento em mãos e abra o link no Chrome ou no Safari.
                </p>
              )}
              {c.verification === "sms" && <p className="mt-2 text-xs text-slate-500">A Autentique enviará um código por SMS para o seu celular ({c.signer.phone}) para confirmar a assinatura.</p>}
              <div className="mt-4 space-y-2.5">
                <label className={`flex items-start gap-3 text-sm ${lido ? "text-slate-700 cursor-pointer" : "text-slate-400"}`}>
                  <Checkbox checked={aceitoC} disabled={!lido} onCheckedChange={(v) => setAceitoC(!!v)} className="mt-0.5" data-testid="contract-accept" /> Li o contrato até o final e concordo com todos os seus termos.
                </label>
                <label className={`flex items-start gap-3 text-sm ${lido ? "text-slate-700 cursor-pointer" : "text-slate-400"}`}>
                  <Checkbox checked={aceitoP} disabled={!lido} onCheckedChange={(v) => setAceitoP(!!v)} className="mt-0.5" data-testid="contract-accept-privacy" /> Li e aceito a Política de Privacidade.
                </label>
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4 space-y-3" data-testid="contract-waiting">
              <p className="text-sm text-slate-700 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-sky-600" /> Aguardando a sua assinatura na Autentique…</p>
              <p className="text-xs text-slate-500">Abrimos a página de assinatura em outra aba. Se não abriu, use o botão abaixo. Assim que você assinar, esta tela libera o acesso sozinha. Se a página da Autentique ficar parada depois de concluir a selfie e a assinatura (por exemplo, no QR code), pode fechá-la e voltar para esta aba.</p>
              <div className="flex flex-wrap gap-2">
                {aut.link && <a href={aut.link} target="_blank" rel="noopener noreferrer"><Button type="button" className="text-white" style={{ background: "#232D4F" }} data-testid="contract-open-link">Abrir página de assinatura</Button></a>}
                <Button type="button" variant="outline" onClick={() => conferir(true)} data-testid="contract-check">Já assinei</Button>
              </div>
            </div>
          )}
        </Passo>
        ) : (
        <Passo n={3} titulo="Assine" ok={!!assin && nomeOk && codigoOk}>
          <Campo label="Digite seu nome completo para confirmar">
            <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Digite aqui o seu nome completo" autoComplete="off" data-testid="contract-typed-name" />
            <p className="text-xs text-slate-500 mt-1">Escreva o seu <strong>nome completo</strong>, igual ao que está no cadastro (etapa 2, acima). Ele vale como a sua assinatura.</p>
          </Campo>
          <div className="mt-4"><Label className="mb-1.5 block">Desenhe sua assinatura</Label><SignaturePad onChange={setAssin} /></div>
          {c.require_code && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-sm text-slate-600 flex items-center gap-2"><Mail className="h-4 w-4 text-[#1E9E2A]" /> Confirmação por e-mail: enviaremos um código de 6 números.</p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button type="button" variant="outline" onClick={enviarCodigo} disabled={busy} data-testid="contract-send-code">{codeSent ? "Reenviar código" : "Enviar código"}</Button>
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" inputMode="numeric" className="w-32 text-center tracking-widest" data-testid="contract-code" />
              </div>
              {codeSent && <p className="text-xs text-slate-400 mt-2">Enviado para {codeSent}. Vale por 10 minutos.</p>}
            </div>
          )}
          <div className="mt-4 space-y-2.5">
            <label className={`flex items-start gap-3 text-sm ${lido ? "text-slate-700 cursor-pointer" : "text-slate-400"}`}>
              <Checkbox checked={aceitoC} disabled={!lido} onCheckedChange={(v) => setAceitoC(!!v)} className="mt-0.5" data-testid="contract-accept" /> Li o contrato até o final e concordo com todos os seus termos.
            </label>
            <label className={`flex items-start gap-3 text-sm ${lido ? "text-slate-700 cursor-pointer" : "text-slate-400"}`}>
              <Checkbox checked={aceitoP} disabled={!lido} onCheckedChange={(v) => setAceitoP(!!v)} className="mt-0.5" data-testid="contract-accept-privacy" /> Li e aceito a Política de Privacidade.
            </label>
          </div>
        </Passo>
        )}

        {erro && <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3" data-testid="contract-error">{erro}</div>}
        {!pronto && !(externo && aut) && faltas.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm px-4 py-3" data-testid="contract-missing">
            <p className="font-semibold">Para liberar o botão de assinar, falta:</p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">{faltas.map((f) => <li key={f}>{f}</li>)}</ul>
          </div>
        )}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 pb-10">
          <p className="text-xs text-slate-400 max-w-md">Sua assinatura eletrônica fica registrada com data, hora, IP e aparelho, e tem validade jurídica nos termos da Lei nº 14.063/2020.</p>
          {!(externo && aut) && (
            <Button onClick={externo ? iniciarExterno : assinar} disabled={!pronto || busy || preview} title={preview ? "Prévia: só a aluna pode assinar" : ""} className="text-white h-12 px-8 font-semibold w-full sm:w-auto" style={{ background: "#1E9E2A" }} data-testid="contract-sign">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><FileSignature className="h-5 w-5 mr-2" /> {externo ? "Continuar para assinar" : "Assinar contrato"}</>}
            </Button>
          )}
        </div>
      </div>
    </Shell>
  );
}

const Shell = ({ children, onLogout, preview = false }) => (
  <div className="fixed inset-0 z-[100] overflow-y-auto bg-[#F8FAFC]" data-testid="contract-gate">
    <header className="h-16 bg-white border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-10">
      <Brand size="md" />
      <Button variant="ghost" size="sm" onClick={onLogout}>{preview ? "Voltar" : "Sair"}</Button>
    </header>
    <main className="px-4 py-6 sm:py-10">{children}</main>
  </div>
);
const Passo = ({ n, titulo, ok, children }) => (
  <section className="bg-white/60 rounded-2xl border border-slate-200 p-4 sm:p-6">
    <h2 className="font-display font-semibold text-slate-900 flex items-center gap-3 mb-4">
      <span className={`h-7 w-7 rounded-full grid place-items-center text-sm text-white ${ok ? "bg-[#1E9E2A]" : "bg-slate-300"}`}>{ok ? "✓" : n}</span> {titulo}
    </h2>
    {children}
  </section>
);
const Campo = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
